/**
 * SHARE-04..SHARE-08 — links do WhatsApp (`wa.me`) e textos de envio de documentos.
 *
 * Sem telefone do paciente (`patients.phone`, PAC-06), o link abre o WhatsApp sem destinatário e
 * o médico escolhe o contato. Com telefone, vai como destinatário e aparece mascarado na UI.
 */
import { getPhoneDigits } from '@/shared/lib/phoneMask'

const WA_BASE = 'https://wa.me/'
const BRAZIL_DDI = '55'

/**
 * Dígitos aceitos pelo `wa.me` (DDI + DDD + número). Aceita E.164 (`+5511…`) ou nacional
 * (`(11) 9…`, recebe o 55). Devolve `null` quando não parece um telefone brasileiro válido.
 */
export function toWhatsAppDigits(phone: string | null | undefined): string | null {
  const digits = getPhoneDigits(phone ?? '')
  if (digits.length === 10 || digits.length === 11) return `${BRAZIL_DDI}${digits}`
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith(BRAZIL_DDI)) return digits
  return null
}

/**
 * PAC-06: telefone do cadastro em E.164 (`+5511987654421`). Vazio → `null`; número que não parece
 * brasileiro → `undefined` (inválido, o formulário avisa).
 */
export function toPatientPhoneE164(raw: string | null | undefined): string | null | undefined {
  if (!getPhoneDigits(raw ?? '')) return null
  const digits = toWhatsAppDigits(raw)
  return digits ? `+${digits}` : undefined
}

/** `tel:` para «Ligar» na ficha; `null` sem telefone válido. */
export function buildTelUrl(phone: string | null | undefined): string | null {
  const digits = toWhatsAppDigits(phone)
  return digits ? `tel:+${digits}` : null
}

/** `https://wa.me/<dígitos>?text=` com número; `https://wa.me/?text=` sem. Texto URL-encoded. */
export function buildWhatsAppUrl({ phoneE164, text }: { phoneE164?: string | null; text: string }): string {
  const digits = toWhatsAppDigits(phoneE164)
  return `${WA_BASE}${digits ?? ''}?text=${encodeURIComponent(text)}`
}

/** Telefone mascarado para toast/subtítulo: celular «(11) 9••••-4421», fixo «(11) ••••-4444». */
export function maskBrPhone(phone: string | null | undefined): string {
  const full = toWhatsAppDigits(phone)
  if (!full) return ''
  const national = full.slice(BRAZIL_DDI.length)
  const ddd = national.slice(0, 2)
  const last4 = national.slice(-4)
  if (national.length === 11) return `(${ddd}) ${national[2]}••••-${last4}`
  return `(${ddd}) ••••-${last4}`
}

/**
 * Texto de envio: «<tipo> de <paciente>» e, se houver, o link em linha própria. O código de acesso
 * do link público continua vindo de `loadClinicalDocumentShareText` (fluxo atual, SHARE-05).
 */
export function buildDocumentShareText({
  kindLabel,
  patientName,
  link,
}: {
  kindLabel: string
  patientName?: string | null
  link?: string | null
}): string {
  const kind = kindLabel.trim() || 'Documento'
  const name = patientName?.trim()
  const head = name ? `${kind} de ${name}` : kind
  const url = link?.trim()
  if (!url) return head
  return [head, '', 'Abra o documento digital:', url].join('\n')
}
